import api from './api';

export type AnalyticsScope = 'all' | 'auto' | 'manual' | 'edge';

export interface DailyPnl { date: string; pnl: number; trades: number }
export interface Bucket   { bucket: string; count: number }

export interface DimRow {
  key: string;
  trades: number;
  wins: number;
  losses: number;
  winRate: number;
  avgR: number;
  totalPnl: number;
  avgPnl: number;
  enoughData: boolean;
}

export interface InsightItem {
  id: string;
  severity: 'good' | 'bad' | 'warn' | 'info';
  title: string;
  detail: string;
  sampleN: number;
}

export interface OptimalRoi {
  avgWinR: number;
  avgLossR: number;
  payoffRatio: number;
  breakevenWinRate: number;
  winRPercentiles: { p50: number; p75: number; p90: number };
  mfeCoveragePct: number;
  captureEfficiencyPct: number | null;
  suggestedTargetR: number | null;
  targetSweep: { r: number; expectancy: number }[];
  leftOnTableR: number | null;
  avgHeatR: number | null;
  heatCoveragePct: number;
}

export interface StrategyBreakdown {
  strategyId: number | null;
  name: string;
  engine: string | null;
  totalTrades: number;
  winRate: number;
  avgR: number;
  totalPnl: number;
}
export interface TagBreakdown {
  tag: string; totalTrades: number; winRate: number; avgR: number; totalPnl: number;
}

export interface AnalyticsSummary {
  scope: AnalyticsScope;
  totalTrades: number;
  wins: number;
  losses: number;
  scratches: number;
  winRate: number;
  avgR: number;
  expectancy: number;
  profitFactor: number;
  totalPnl: number;
  totalWinPnl: number;
  totalLossPnl: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  longestWinStreak: number;
  longestLossStreak: number;
  meanHoldHours: number;
  medianHoldHours: number;
  captureEfficiencyPct: number | null;
  headlineVerdict: string;
  benchmark: { date: string; retPct: number }[];
  benchmarkRetPct: number | null;
  dailyPnl: DailyPnl[];
  rDistribution: Bucket[];
  holdHistogram: Bucket[];
  rollingExpectancy: { t: string; r: number; winRate: number }[];
  bySymbol: DimRow[];
  bySide: DimRow[];
  byExitMethod: DimRow[];
  byTimeframe: DimRow[];
  byStrength: DimRow[];
  byEntryCond: DimRow[];
  byHoldBucket: DimRow[];
  byDayOfWeek: DimRow[];
  byHour: DimRow[];
  byStrategy: StrategyBreakdown[];
  tagBreakdown: TagBreakdown[];
  optimalRoi: OptimalRoi;
  insights: InsightItem[];
}

export interface ExplorerTradeRow {
  id: number;
  tradeId: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  strategyId: number | null;
  realizedPnl: number;
  rMultiple: number;
  roiPct: number;
  holdHours: number | null;
  exitCategory: string;
  timeframeMin: number;
  entryStrength: string | null;
  openedAt: string | null;
  closedAt: string | null;
}

export interface StrategyComparisonRow {
  strategyId: number; name: string; engine: string | null;
  total: number; buys: number; shorts: number; flats: number;
}

export const analyticsService = {
  getSummary: (days = 30, scope: AnalyticsScope = 'all') =>
    api.get<{ success: boolean; data: AnalyticsSummary }>(`/analytics/summary`, { params: { days, scope } })
      .then(r => r.data?.data),

  getTrades: (params: { days?: number; scope?: AnalyticsScope; sort?: string; limit?: number; dim?: string; dimValue?: string }) =>
    api.get<{ success: boolean; data: ExplorerTradeRow[] }>(`/analytics/trades`, { params })
      .then(r => r.data?.data || []),

  getStrategyComparison: (days = 30) =>
    api.get<{ success: boolean; data: { sinceDays: number; strategies: StrategyComparisonRow[] } }>(`/analytics/strategy-comparison`, { params: { days } })
      .then(r => r.data?.data),
};
